import {
	Avatar,
	Box,
	Center,
	createStyles,
	getDefaultZIndex,
	Group,
	ScrollArea,
	Skeleton,
	Stack,
	useMantineTheme,
} from '@mantine/core';
import { useDebouncedValue, useDidUpdate } from '@mantine/hooks';
import type { TeamOut } from '@repo/api-codegen';
import { apiQueryKey, useApiListMemberships } from '@repo/api-codegen';
import { titleCase } from '@repo/common/utils';
import { Button, Text } from '@repo/foundations';
import { useCallback, useState } from 'react';
import { queryClient } from '../../api';
import { getDisplayName } from '../../utils/userUtils';
import { EmptyState } from '../EmptyState';
import { InviteTeamMemberForm } from '../InviteTeamMemberForm/InviteTeamMemberForm';
import { MembershipSelector } from '../TeamSettingsModal/MembershipSelector';
import { UserAvatar } from '../UserAvatar';

interface ITeamMembersListModal {
	team: TeamOut;
}

const useStyles = createStyles((theme) => ({
	searchContainer: {
		position: 'sticky',
		top: 0,
		zIndex: getDefaultZIndex('modal'),
		paddingBottom: theme.spacing.md,
	},
	listContainer: {
		height: theme.other.space[160],
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	},
}));

export function TeamMembersList({ team }: ITeamMembersListModal) {
	const { classes } = useStyles();
	const theme = useMantineTheme();
	const [currentPage, setCurrentPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500);

	const { data, isLoading, isError } = useApiListMemberships({
		pathParams: {
			teamId: team.id,
		},
		queryParams: { page: currentPage, search_term: debouncedSearchTerm },
	});

	const handleNextPage = useCallback(() => {
		setCurrentPage((prevPage) =>
			Math.min(prevPage + 1, data?.total_pages ?? 1)
		);
	}, [data?.total_pages]);

	const handlePrevPage = useCallback(() => {
		setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
	}, []);

	// Reset pagination when search changes
	useDidUpdate(() => {
		setCurrentPage(1);
	}, [debouncedSearchTerm]);

	const handleFinish = useCallback((teamId: string) => {
		// Invalidate queries to refresh the list after adding members
		queryClient.invalidateQueries({
			queryKey: apiQueryKey('auth/teams/{team_id}/memberships', {
				team_id: teamId,
			}),
		});
		setSearchTerm('');
		setCurrentPage(1);
	}, []);

	if (isLoading) {
		return (
			<Center>
				<Skeleton height={theme.other.space[90]} w={'100%'} />
			</Center>
		);
	}

	if (isError) {
		return null;
	}

	return (
		<Stack spacing={0}>
			<Box className={classes.searchContainer}>
				<InviteTeamMemberForm
					team={team}
					searchValue={searchTerm}
					setSearchValue={setSearchTerm}
					placeholder="Search for or add new team members"
					onFinish={handleFinish}
				/>
			</Box>
			<Box className={classes.listContainer}>
				{data?.results?.length === 0 ? (
					<EmptyState
						title={'No existing memberships found'}
						description={'Try adjusting your search query'}
						includeGoBack={false}
						size={'sm'}
					/>
				) : (
					<ScrollArea.Autosize
						offsetScrollbars
						style={{ flex: 1, minHeight: 0 }}
					>
						<Stack spacing={0}>
							{data?.results?.map((membership) => (
								<Group
									position="apart"
									key={membership.id}
									py={8}
									sx={{
										borderBottom: `1px solid ${theme.other.getColor('border/secondary/default')}`,
										'&:last-child': {
											borderBottom: 'none',
										},
									}}
								>
									{membership.user && (
										<Group noWrap w={204} spacing={8}>
											<UserAvatar user={membership.user} enableLink size="md" />
											<Stack spacing={0}>
												<Text size="sm" weight="semibold" lineClamp={1}>
													{getDisplayName(membership.user)}
												</Text>
												<Text
													size="xs"
													color="text/secondary/default"
													lineClamp={1}
												>
													{!membership.read
														? 'Pending request'
														: titleCase(membership.user.role)}
												</Text>
											</Stack>
										</Group>
									)}
									{!membership.user && membership.user_group && (
										<Group noWrap w={204} spacing={8}>
											<Avatar size="md">{membership.user_group.icon}</Avatar>
											<Stack spacing={0}>
												<Text size="sm" weight="semibold" lineClamp={1}>
													{membership.user_group.name}
												</Text>
												<Text size="xs" color="text/secondary/default">
													{membership.user_group?.users?.length} members
												</Text>
											</Stack>
										</Group>
									)}
									<Group position="right">
										<MembershipSelector membership={membership} />
									</Group>
								</Group>
							))}
						</Stack>
						{data?.total_pages !== 1 && (
							<Group position="center" mt="md">
								<Button
									size="sm"
									onClick={handlePrevPage}
									disabled={currentPage === 1}
									aria-label="Previous Page"
								>
									Prev
								</Button>
								<Text size="xs">{`Page ${currentPage} of ${data?.total_pages}`}</Text>
								<Button
									size="sm"
									onClick={handleNextPage}
									disabled={currentPage === data?.total_pages}
									aria-label="Next Page"
								>
									Next
								</Button>
							</Group>
						)}
					</ScrollArea.Autosize>
				)}
			</Box>
		</Stack>
	);
}
